import { Inject, Injectable, InjectionToken, Optional, Pipe, PipeTransform } from '@angular/core';
import { FileRecordDto } from '@careapp-group/ng-sdk';
import { Store } from '@ngrx/store';
import { isAfter, parseISO } from 'date-fns';
import { distinct, filter, map, tap } from 'rxjs/operators';
import { FilesSelectors } from '../../store/files/files-selectors';
import { requestDownloadUrl } from '../../store/files/files.actions';
import { asapScheduler, of } from 'rxjs';
import { environment } from '../../../environments/environment';

export type FileDownloadMode = 'get' | 'downloadUrl';
export const FILE_DOWNLOAD_PIPE_DOWNLOAD_MODE = new InjectionToken<FileDownloadMode>(
  'FILE_DOWNLOAD_PIPE_DOWNLOAD_MODE'
);

@Pipe({
  name: 'filedownloadurl',
  pure: true,
  standalone: false,
})
@Injectable({ providedIn: 'root' })
export class FileDownloadUrlPipeV2 implements PipeTransform {
  constructor(
    private store: Store,
    @Optional()
    @Inject(FILE_DOWNLOAD_PIPE_DOWNLOAD_MODE)
    private mode: FileDownloadMode = 'downloadUrl'
  ) {}

  public transform(input: string | Pick<FileRecordDto, 'variants' | 'id'>, size = 'sizeMedium') {
    if (!input) {
      return of('');
    }

    // If input is a string, assume it is an id and wrap in an object.
    const object = typeof input === 'string' ? { id: input } : input;

    // If input is an object, with the key 'variants', find the expected variant (or default to sizeMedium).
    if ('variants' in object && object.variants) {
      const variant = object.variants.find((v) => v.purpose === size);
      if (variant) {
        return this.getDownloadUrl(variant);
      }
    }

    return this.getDownloadUrl(object);
  }

  getDownloadUrl(file?: { id: string }) {
    if (this.mode === 'get') {
      return of(`${environment.API_V2_BASE_PATH}/v2/files/${file.id}/get`);
    } else {
      if (!!file.id) {
        return this.store.select(FilesSelectors.getFilesState).pipe(
          tap(() =>
            asapScheduler.schedule(() => this.store.dispatch(requestDownloadUrl({ id: file.id })))
          ),
          map((filesState) => filesState.urls[file.id]),
          filter((url) => url && (!url.expiresAt || isAfter(parseISO(url.expiresAt), Date.now()))),
          map((url) => url.url),
          distinct()
        );
      }
      return null;
    }
  }
}
